import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

/**
 * What is outstanding on the signed-in person's record.
 *
 * Shared by the profile's AttentionStrip and the home page's AttentionCard so
 * the two can never disagree. The rules (see AttentionStrip for the why):
 * a failed source is OMITTED, never reported as zero; available:false means
 * "unknown", not "nothing missing". completeness follows the same rule: it is
 * only computed when BOTH reads succeeded and are known.
 *
 * `refreshKey` re-reads both sources when it changes. The profile bumps it
 * after a personal-details save or a document upload: the strip stays mounted
 * across tab switches, so without it a just-filled field kept showing as
 * missing until a full page reload. The previous items stay on screen while
 * the re-read is in flight rather than flashing empty.
 */
export default function useAttentionSignals(refreshKey = 0) {
  const [state, setState] = useState({ loading: true, items: [], completeness: null });

  useEffect(() => {
    let active = true;
    (async () => {
      const [docsResult, personalResult] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/profile/documents`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/api/profile/personal`, { withCredentials: true }),
      ]);
      if (!active) return;

      const items = [];
      let docs = null;
      let personal = null;

      if (docsResult.status === 'fulfilled') {
        const d = docsResult.value.data;
        if (d?.available && Array.isArray(d.documents)) {
          docs = d.documents;
          const missing = docs.filter((x) => !x.is_uploaded);
          if (missing.length > 0) {
            items.push({ id: 'documents', tab: 'documents', count: missing.length, labels: missing.map((x) => x.label) });
          }
        }
      }

      if (personalResult.status === 'fulfilled') {
        const p = personalResult.value.data;
        if (p?.available && p.fields) {
          personal = p.fields;
          if (!personal.emergency_contact) items.push({ id: 'emergency', tab: 'personal' });
          if (!personal.private_street) items.push({ id: 'address', tab: 'personal' });
        }
      }

      const completeness = docs && personal
        ? {
          done: docs.filter((x) => x.is_uploaded).length
            + (personal.emergency_contact ? 1 : 0)
            + (personal.private_street ? 1 : 0),
          total: docs.length + 2,
        }
        : null;

      setState({ loading: false, items, completeness });
    })();
    return () => { active = false; };
  }, [refreshKey]);

  return state;
}
